import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, Shield, Loader2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import ReCAPTCHA from 'react-google-recaptcha';

const JOTFORM_URL = "https://form.jotform.com/261665715977069";
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function CaptchaModal({ isOpen, onClose }) {
  const [verificationState, setVerificationState] = useState('idle'); // 'idle' | 'verified'
  const [error, setError] = useState('');
  const recaptchaRef = useRef(null);
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setVerificationState('idle');
      setError('');
      hasRedirected.current = false;
      // Don't reset reCAPTCHA here to avoid triggering onChange with null
    }
  }, [isOpen]);

  const handleRecaptchaChange = (token) => {
    // Guard: do nothing if modal is closed or already redirected
    if (!isOpen || hasRedirected.current) {
      return;
    }

    if (token) {
      hasRedirected.current = true;
      setVerificationState('verified');
      setError('');
      
      // Open the form in new tab after brief delay to show verified state
      setTimeout(() => {
        // Simply open the form - don't try to detect if popup was blocked
        window.open(JOTFORM_URL, '_blank');
        
        // Close modal after a short delay
        setTimeout(() => {
          onClose();
        }, 1000);
      }, 800);
    }
    // Don't set error on null token - this can happen during reset
  };

  const handleRecaptchaExpired = () => {
    if (!isOpen) return;
    setVerificationState('idle');
    hasRedirected.current = false;
    setError('Verification expired. Please verify again.');
  };

  const handleRecaptchaError = (err) => {
    if (!isOpen) return;
    setVerificationState('idle');
    hasRedirected.current = false;
    setError('Verification error. Please try again.');
  };

  const handleManualRedirect = () => {
    window.open(JOTFORM_URL, '_blank');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={(e) => {
          // Only close if clicking the backdrop and not during verification
          if (verificationState === 'idle') {
            onClose();
          }
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-skytec-bg border border-skytec-border rounded-2xl shadow-2xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Shield className="w-6 h-6 text-skytec-primary mr-2" />
              <h3 className="font-heading text-xl font-bold text-skytec-fg">Verification Required</h3>
            </div>
            {verificationState === 'idle' && (
              <button
                onClick={onClose}
                className="text-skytec-fg/60 hover:text-skytec-fg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {verificationState === 'idle' && (
            <>
              <p className="text-skytec-fg/70 mb-6">
                Please complete the verification below to proceed with your application.
              </p>

              {/* Google reCAPTCHA */}
              <div className="flex justify-center mb-4">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={handleRecaptchaChange}
                  onExpired={handleRecaptchaExpired}
                  onErrored={handleRecaptchaError}
                  theme="light"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm mb-4 text-center">{error}</p>
              )}

              <div className="flex justify-center">
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="border-skytec-border text-skytec-fg hover:bg-skytec-surface"
                >
                  Cancel
                </Button>
              </div>

              <p className="text-skytec-fg/50 text-xs text-center mt-4">
                Protected by Google reCAPTCHA
              </p>
            </>
          )}

          {verificationState === 'verified' && (
            <div className="text-center py-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 bg-skytec-accent/20 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <CheckCircle className="w-8 h-8 text-skytec-accent" />
              </motion.div>
              <h4 className="font-heading text-lg font-bold text-skytec-fg mb-2">Verified!</h4>
              <p className="text-skytec-fg/70 mb-4">
                Opening application form...
              </p>
              
              {/* Always show manual redirect button as fallback */}
              <Button
                onClick={handleManualRedirect}
                className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Application Form
              </Button>
              
              <p className="text-skytec-fg/50 text-xs mt-4">
                If the form didn't open, click the button above or check your popup blocker.
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
